import { useMemo } from "react";
import { SelectableChipGroup, ChipOption } from "@platform/design-system";

import Section from "./Section";
import SelectionActions from "./SelectionActions";
import { SendRfpCoverage } from "./api/types";

export interface CoveragesSectionProps {
  coverages: SendRfpCoverage[];
  selectedCoverages: string[];
  onCoveragesChange: (coverages: string[]) => void;
  onNext: () => void;
  onBack: () => void;
  active?: boolean;
  completed?: boolean;
}

const CoveragesSection = ({
  coverages,
  selectedCoverages,
  onCoveragesChange,
  onNext,
  onBack,
  active = false,
  completed = false,
}: CoveragesSectionProps): React.JSX.Element => {
  // Transform SendRfpCoverage to ChipOption format, marking incomplete as disabled
  const chipOptions: ChipOption[] = useMemo(
    () =>
      coverages.map((coverage) => ({
        id: coverage.id,
        label:
          coverage.isComplete === false
            ? `${coverage.label} (incomplete)`
            : coverage.label,
        disabled: coverage.isComplete === false,
      })),
    [coverages],
  );

  // Get IDs of complete coverages only
  const completeCoverageIds = useMemo(
    () => coverages.filter((c) => c.isComplete !== false).map((c) => c.id),
    [coverages],
  );

  const handleSelectAll = (): void => {
    // Only select complete coverages
    onCoveragesChange(completeCoverageIds);
  };

  const handleClear = (): void => {
    onCoveragesChange([]);
  };

  const handleToggleCoverage = (id: string): void => {
    // Prevent toggling incomplete coverages
    const coverage = coverages.find((c) => c.id === id);
    if (coverage?.isComplete === false) {
      return;
    }

    if (selectedCoverages.includes(id)) {
      onCoveragesChange(selectedCoverages.filter((covId) => covId !== id));
    } else {
      onCoveragesChange([...selectedCoverages, id]);
    }
  };

  return (
    <Section
      title="Coverages"
      onNext={onNext}
      onBack={onBack}
      nextLabel="Next"
      nextDisabled={selectedCoverages.length === 0}
      active={active}
      completed={completed}
    >
      <SelectionActions
        onSelectAll={handleSelectAll}
        onClear={handleClear}
        selectAllDisabled={!completeCoverageIds.length}
        clearDisabled={!selectedCoverages.length}
      />
      <SelectableChipGroup
        options={chipOptions}
        selected={selectedCoverages}
        onChange={handleToggleCoverage}
      />
    </Section>
  );
};

export default CoveragesSection;
